import { Input, InputProps, InputRef } from '@unistocks/components/basic/input';
import {
  FormFieldInjectedProps,
  withLabelAndValidation
} from '@unistocks/components/form/components/with-label-and-validation';
import { FormComponentPropsBase, FormFieldOmittedProps } from '@unistocks/components/form/model';
import { isFunction } from '@unistocks/shared/utils/util';
import { memo } from 'react';
import { FieldValues } from 'react-hook-form';
import { useComposedRefs, useControllableState } from 'tamagui';

export type FormInputProps<TFieldValues extends FieldValues = FieldValues> = Omit<
  InputProps,
  FormFieldOmittedProps | 'readOnly'
> &
  FormComponentPropsBase<TFieldValues> & {
    iconAfter?: React.ReactNode;
    appendText?: string;
  };

const FormInputComponent = memo<FormInputProps & FormFieldInjectedProps>(
  ({
    ref: inputRef,
    name,
    id,
    label,
    helpText,
    hidden,
    dependsOn,
    iconAfter,
    appendText,
    valueTransform,
    field,
    fieldState,
    isRequired,
    isDisabled,
    onChangeText,
    ...inputProps
  }) => {
    const composedRef = useComposedRefs(inputRef, field.ref);

    const [inputValue, onInput] = useControllableState<string>({
      prop: valueTransform?.input?.(field.value) ?? field.value?.toString(),
      defaultProp: '',
      strategy: 'prop-wins',
      onChange: (nextValue: string) => {
        let transformedValue = nextValue;
        if (isFunction(valueTransform?.output)) {
          transformedValue = valueTransform.output(nextValue);
        }

        field.onChange(transformedValue);
        onChangeText?.(nextValue);
      }
    });

    return (
      <Input disabled={isDisabled} invalid={fieldState.invalid}>
        <Input.Box>
          <Input.Section>
            <Input.Area
              ref={composedRef}
              id={id}
              enablesReturnKeyAutomatically={isRequired}
              onBlur={field.onBlur}
              {...inputProps}
              value={inputValue}
              onChangeText={onInput}
            />
          </Input.Section>
          {iconAfter ? (
            <Input.Section>
              <Input.Icon>{iconAfter}</Input.Icon>
            </Input.Section>
          ) : null}
          {appendText ? (
            <Input.Section>
              <Input.AppendText>{appendText}</Input.AppendText>
            </Input.Section>
          ) : null}
        </Input.Box>
      </Input>
    );
  }
);

FormInputComponent.displayName = 'FormInput';

export const FormInput = withLabelAndValidation<InputRef, FormInputProps>(FormInputComponent) as <
  TFieldValues extends FieldValues = FieldValues
>(
  props: FormInputProps<TFieldValues>
) => React.ReactElement<FormInputProps<TFieldValues>>;
